import { useState, useEffect, useCallback } from 'react';
import { templateService } from './templateService';
import { normalizePhoneDigits, isPhoneOptedOut } from '../utils/optoutUtils';

export { normalizePhoneDigits, isPhoneOptedOut };

let cachedOptedOutSet = null;
let lastFetchTime = 0;
const CACHE_TTL_MS = 60 * 1000; // 1 minute

export function invalidateOptoutCache() {
  cachedOptedOutSet = null;
  lastFetchTime = 0;
}

export async function getOptedOutPhoneSet(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && cachedOptedOutSet && now - lastFetchTime < CACHE_TTL_MS) {
    return cachedOptedOutSet;
  }

  try {
    const res = await templateService.getOptinList();
    const list =
      res?.status === 200
        ? Array.isArray(res.data)
          ? res.data
          : res.data
            ? [res.data]
            : []
        : Array.isArray(res)
          ? res
          : res
            ? [res]
            : [];

    const set = new Set();
    list.forEach((item) => {
      const isOptin = item?.['Opted In'] ?? item?.is_optin;
      if (isOptin === false || isOptin === 'false' || isOptin === 0 || isOptin === '0') {
        const rawPhone = item?.['Phone Number'] || item?.phone_number;
        if (rawPhone) {
          const digits = normalizePhoneDigits(rawPhone);
          if (digits) {
            set.add(digits);
            if (digits.length >= 10) {
              set.add(digits.slice(-10));
            }
          }
        }
      }
    });

    cachedOptedOutSet = set;
    lastFetchTime = now;
    return set;
  } catch (error) {
    console.error('Failed to load opt-in list for optout check', error);
    return cachedOptedOutSet || new Set();
  }
}

export async function checkPhoneOptedOut(phone, forceRefresh = false) {
  const set = await getOptedOutPhoneSet(forceRefresh);
  return isPhoneOptedOut(phone, set);
}

export function useOptoutCheck() {
  const [optedOutSet, setOptedOutSet] = useState(() => cachedOptedOutSet || new Set());
  const [isLoading, setIsLoading] = useState(() => !cachedOptedOutSet);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const set = await getOptedOutPhoneSet(true);
      setOptedOutSet(set);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    getOptedOutPhoneSet().then((set) => {
      if (isMounted) {
        setOptedOutSet(set);
        setIsLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const checkIsOptedOut = useCallback(
    (phone) => {
      return isPhoneOptedOut(phone, optedOutSet);
    },
    [optedOutSet]
  );

  return {
    isOptedOut: checkIsOptedOut,
    isLoading,
    refresh,
    optedOutSet,
  };
}

